import { useEffect, useRef, useState } from 'react';
import { Send } from 'lucide-react';
import { motion } from 'framer-motion';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { app } from '../Admin/Config/Config';
import { DotLottieReact } from '@lottiefiles/dotlottie-react';
import { getAuth } from 'firebase/auth';

export default function AiChat() {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [isTyping, setIsTyping] = useState(false);

  const getAppreciationTag = () => {
    const tags = [
      { text: "Your sentence is perfect ✓", color: "text-emerald-400" },
      {
        text: "Your sentence is good ✓",
        color: "text-emerald-400",
      },
      {
        text: "Your sentence is great ✓ ",
        color: "text-emerald-400",
      },
      {
        text: "Your sentence is excellent ✓",
        color: "text-emerald-400",
      },
      {
        text: " Your sentence is amazing ✓",
        color: "text-emerald-400",
      },
    ];
    return tags[Math.floor(Math.random() * tags.length)];
  };

  const functions = getFunctions(app, "asia-south1");
  const generateContent = httpsCallable(functions, "gemeni");

  const handleSend = async () => {
    if (!input.trim()) return;

    const userMessage = { 
      text: input, 
      sender: "user",
    };
    setMessages(prevMessages => [...prevMessages, userMessage]);
    setInput("");

    setIsTyping(true);

    try {
      const result = await generateContent({ prompt: input, conversation: messages });
    setMessages((prevMessages) => {
      const updatedMessages = [...prevMessages];
      const userMessageIndex = updatedMessages.length - 1;
      updatedMessages[userMessageIndex] = {
        ...updatedMessages[userMessageIndex],
        corrected: result.data.corrected,
        explanation: result.data.explanation,
        button: "feedback",
        appreciation: getAppreciationTag(),
      };

      // Add AI message
      const aiMessage = {
        text: result.data.response,
        sender: "bot",
      };
      return [...updatedMessages, aiMessage];
    });
    } catch (error) {
      console.error("Error calling function:", error);
      const errorMessage = { text: "Sorry, Britto isn't available now.", sender: "bot" };
      setMessages(prevMessages => [...prevMessages, errorMessage]);
    } finally {
      setIsTyping(false);
    }
  };

  const messagesEndRef = useRef(null);
const skipScrollRef = useRef(false);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

useEffect(() => {
    if (skipScrollRef.current) {
      skipScrollRef.current = false;
      return; 
    }

  const chatContainer = document.querySelector(".chat-container");
  if (chatContainer) {
    if (chatContainer.scrollHeight <= chatContainer.clientHeight) {
      chatContainer.scrollTop = 0;
    } else {
      scrollToBottom();
    }
  }
}, [messages]);

const handleUpdateAppreciation = (index, value) => {
  skipScrollRef.current = true;
  setMessages((prevMessages) =>
    prevMessages.map((msg, i) =>
      i === index ? { ...msg, button: value } : msg
    )
  );
};

// ==================== Text Formating Logic ====================
const formatInline = (text) => {
  text = text.replace(/\*\*\*(.+?)\*\*\*/g, "<strong><em>$1</em></strong>");
  text = text.replace(/___(.+?)___/g, "<strong><em>$1</em></strong>");
  text = text.replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>");
  text = text.replace(/__(.+?)__/g, "<strong>$1</strong>");
  text = text.replace(/\*(.+?)\*/g, "<em>$1</em>");
  text = text.replace(/_(.+?)_/g, "<em>$1</em>");
  text = text.replace(/`(.+?)`/g,"<code class='px-1 bg-gray-800 rounded text-emerald-400'>$1</code>");
  text = text.replace(/\[([^\]]+)\]\(([^)]+)\)/g,
    `<a href="$2" target="_blank" rel="noopener noreferrer" class="text-emerald-400 underline">$1</a>`
  );
    text = text.replace(/~~(.+?)~~/g, "<del>$1</del>");

  return text;
};


  return (
    <div className="w-full max-w-7xl mx-auto px-2 p-2 sm:px-4 md:px-6">
      <div className="bg-black rounded-xl flex flex-col h-[80vh] sm:h-[75vh] md:h-[640px]  w-full max-w-7xl mx-auto relative overflow-hidden">
        <div className="flex-1 overflow-y-auto p-2 sm:p-4 md:p-6 pb-28 sm:pb-32 md:pb-36 lg:pb-40 space-y-3 sm:space-y-4 md:space-y-6 chat-container">
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
            className="flex justify-start w-full"
          >
            <div className="relative w-full sm:w-[800px] mr-auto">
              <div className="relative p-3 sm:p-5 text-sm sm:text-md bg-gradient-to-br from-black via-gray-950/90 to-black/95 border border-gray-800/40 rounded-xl text-gray-200 backdrop-blur-xl shadow-xl">
                <div className="flex items-center justify-start mb-4">
                  <div className="flex items-center gap-3">
                    <motion.div
                      className="w-9 h-9 overflow-hidden rounded-full ring-2 ring-emerald-500/20 flex-shrink-0 bg-black/80 shadow-lg"
                      animate={{ scale: [1, 1.05, 1] }}
                      transition={{ repeat: Infinity, duration: 2 }}
                    >
                      <DotLottieReact
                        src="https://lottie.host/04f83f38-032d-43bb-badd-c59980d16a44/NIiDZzeZ6Y.lottie"
                        loop
                        autoplay
                      />
                    </motion.div>
                    <div className="font-medium text-sm md:text-base text-emerald-400">
                      Britto AI
                    </div>
                  </div>
                </div>
                <div className="border-t border-dotted border-gray-800/60 my-4"></div>
                <div className="text-gray-200 text-sm md:text-[15px] leading-relaxed tracking-wide">
                  Practice English with me! I'm your AI buddy—type something to
                  start chatting.
                </div>
              </div>
            </div>
          </motion.div>

          {messages.map((msg, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
              className={`flex ${
                msg.sender === "user" ? "justify-end" : "justify-start"
              } w-full`}
            >
              <div
                className={`relative w-full sm:w-[800px] ${
                  msg.sender === "user" ? "ml-auto" : "mr-auto"
                }`}
              >
                <div
                  className={`relative p-3 sm:p-5 text-sm sm:text-md ${
                    msg.sender === "user"
                      ? "bg-gradient-to-br from-emerald-950/40 via-gray-900/50 to-emerald-950/40 border border-emerald-500/20 rounded-xl"
                      : "bg-gradient-to-br from-black via-gray-950/90 to-black/95 border border-gray-800/40 rounded-xl"
                  } text-gray-200 backdrop-blur-xl shadow-xl`}
                >
                  <div
                    className={`flex items-center ${
                      msg.sender === "user" ? "justify-end" : "justify-start"
                    } mb-3 sm:mb-4`}
                  >
                    {msg.sender === "user" ? (
                      <div className="flex items-center gap-2 sm:gap-3">
                        <div className="font-medium text-xs sm:text-sm md:text-base text-emerald-300">
                          {getAuth().currentUser.displayName}
                        </div>
                        <div className="w-6 h-6 sm:w-7 sm:h-7 overflow-hidden rounded-full shadow-lg ring-2 ring-emerald-500/20 flex-shrink-0 bg-black/80">
                          <img
                            src="https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcQACGFpr0iqURE_6EHYMm-AGXfhXC1Nzf4ucA&s"
                            alt="User"
                            className="w-full h-full object-cover opacity-90"
                          />
                        </div>
                      </div>
                    ) : (
                      <div className="flex items-center gap-2 sm:gap-3">
                        <motion.div
                          className="w-6 h-6 sm:w-7 sm:h-7 overflow-hidden rounded-full ring-2 ring-emerald-500/20 flex-shrink-0 bg-black/80 shadow-lg"
                          animate={{ scale: [1, 1.05, 1] }}
                          transition={{ repeat: Infinity, duration: 2 }}
                        >
                          <DotLottieReact
                            src="https://lottie.host/04f83f38-032d-43bb-badd-c59980d16a44/NIiDZzeZ6Y.lottie"
                            loop
                            autoplay
                          />
                        </motion.div>
                        <div className="font-medium text-xs sm:text-sm md:text-base text-emerald-400">
                          Britto AI
                        </div>
                      </div>
                    )}
                  </div>
                  {msg.sender === "user" && (
                    <>
                      <div className="border-t border-dotted border-emerald-500/20 my-3 sm:my-4"></div>
                      <div className="flex items-center gap-2 sm:gap-4">
                        <div className="inline-flex items-center px-2 sm:px-3 py-1 sm:py-1.5 rounded-lg bg-emerald-950/50 border border-emerald-500/20">
                          <span className="text-xs font-medium text-emerald-400">
                            Q:
                          </span>
                        </div>
                        <div className="text-gray-200 text-xs sm:text-sm md:text-[15px] leading-relaxed tracking-wide flex-1">
                          {msg.text}
                        </div>
                      </div>
                      {msg?.corrected && (
                        <>
                          <div className="flex flex-wrap items-center gap-2 sm:gap-3 mt-3 sm:mt-4">
                            <div className="flex items-center gap-2 sm:gap-3">
                              <button
                                onClick={() => {
                                  handleUpdateAppreciation(index, "feedback");
                                }}
                                className={`px-3 sm:px-4 py-1.5 sm:py-2 text-xs sm:text-sm transition-all rounded-lg ${
                                  msg.button === "feedback"
                                    ? "bg-emerald-950/60 text-emerald-400 ring-1 ring-emerald-500/30"
                                    : "bg-black/40 text-gray-400 ring-1 ring-gray-800 hover:bg-black/60 hover:text-emerald-400"
                                }`}
                              >
                                Feedback
                              </button>
                              <button
                                onClick={() => {
                                  handleUpdateAppreciation(
                                    index,
                                    "explanation"
                                  );
                                }}
                                className={`relative px-3 sm:px-4 py-1.5 sm:py-2 text-xs sm:text-sm transition-all rounded-lg overflow-hidden ${
                                  msg.button === "explanation"
                                    ? "bg-purple-950/60 text-purple-400 ring-1 ring-purple-500/30"
                                    : "bg-black/40 text-gray-400 ring-1 ring-gray-800 hover:bg-black/60 hover:text-purple-400"
                                }`}
                              >
                                {msg.button === "feedback" && (
                                  <>
                                    <motion.div
                                      className="absolute inset-0 rounded-lg"
                                      initial={{ opacity: 0 }}
                                      animate={{
                                        opacity: [0, 0.8, 0],
                                        scale: [0.98, 1.02, 0.98],
                                      }}
                                      transition={{
                                        repeat: Infinity,
                                        duration: 2,
                                        ease: "easeInOut",
                                      }}
                                    >
                                      <div className="absolute inset-0 rounded-lg border-2 border-purple-400/50"></div>
                                      <div className="absolute inset-0 rounded-lg border border-purple-400/30 blur-[2px]"></div>
                                      <div className="absolute inset-0 rounded-lg bg-gradient-to-r from-purple-400/10 via-fuchsia-400/10 to-purple-400/10"></div>
                                    </motion.div>
                                    <motion.div
                                      className="absolute inset-0 rounded-lg overflow-hidden"
                                      initial={{ opacity: 0 }}
                                      animate={{ opacity: 1 }}
                                    >
                                      <motion.div
                                        className="absolute inset-0 bg-gradient-to-r from-transparent via-purple-400/20 to-transparent"
                                        animate={{
                                          x: ["-100%", "100%"],
                                        }}
                                        transition={{
                                          repeat: Infinity,
                                          duration: 1.5,
                                          ease: "linear",
                                        }}
                                      />
                                    </motion.div>
                                  </>
                                )}
                                <span className="relative z-10">
                                  Explanation
                                </span>
                              </button>
                            </div>
                          </div>
                          {msg.button === "feedback" ? (
                            <div className="flex items-start gap-3 mt-3 sm:mt-4">
                              <div className="flex items-center gap-1.5">
                                <svg
                                  width="16"
                                  height="16"
                                  viewBox="0 0 24 24"
                                  fill="none"
                                  xmlns="http://www.w3.org/2000/svg"
                                  className="text-sky-400"
                                >
                                  <path
                                    d="M20 6L9 17L4 12"
                                    stroke="currentColor"
                                    strokeWidth="2.5"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                  />
                                </svg>
                                <span className="text-xs font-medium bg-gradient-to-r from-sky-400 via-blue-400 to-cyan-400 bg-clip-text text-transparent">
                                  Correct
                                </span>
                              </div>
                              <div className="text-gray-200 text-xs sm:text-sm md:text-[15px] leading-relaxed tracking-wide flex-1">
                                {msg?.corrected ?? ""}
                              </div>
                            </div>
                          ) : msg.button === "explanation" ? (
                            <div className="text-gray-200 text-xs sm:text-sm md:text-[15px] leading-relaxed mt-3 sm:mt-4 tracking-wide">
                              {msg?.explanation ?? ""}
                            </div>
                          ) : null}
                        </>
                      )}
                    </>
                  )}
                  {msg.sender === "bot" && (
                    <>
                      <div className="border-t border-dotted border-gray-800/60 my-4"></div>
                      <div className="text-gray-200 text-xs sm:text-sm md:text-[15px] leading-relaxed tracking-wide">
                        {msg.text.split("\n").map((line, i) => (
                          <div key={i}>
                            {line.startsWith("- ") || line.startsWith("• ") ? (
                              // 🔹 Unordered List
                              <div className="flex items-start gap-2 my-1">
                                <span className="text-white mt-1">•</span>
                                <span
                                  className="text-white mt-1"
                                  dangerouslySetInnerHTML={{
                                    __html: formatInline(line.substring(2)),
                                  }}
                                />
                              </div>
                            ) : line.match(/^\d+\./) ? (
                              // 🔹 Ordered List
                              <div className="flex items-start gap-2 my-1">
                                <span className="text-white mt-1">
                                  {line.match(/^\d+\./)[0]}
                                </span>
                                <span
                                  className="text-white mt-1"
                                  dangerouslySetInnerHTML={{
                                    __html: formatInline(
                                      line.replace(/^\d+\.\s*/, "")
                                    ),
                                  }}
                                />
                              </div>
                            ) : line.startsWith("> ") ? (
                              // 🔹 Blockquote
                              <blockquote
                                className="border-l-4 border-gray-500 pl-3 italic text-gray-400 my-2"
                                dangerouslySetInnerHTML={{
                                  __html: formatInline(line.substring(2)),
                                }}
                              />
                            ) : (
                              // 🔹 Normal text with inline formatting
                              <div
                                className="my-2"
                                dangerouslySetInnerHTML={{
                                  __html: formatInline(line),
                                }}
                              />
                            )}
                          </div>
                        ))}
                      </div>
                    </>
                  )}
                </div>
                {msg.sender === "user" &&
                  !msg.corrected &&
                  msg.appreciation && (
                    <motion.div
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.3, duration: 0.2 }}
                      className={`absolute -bottom-6 right-0 text-xs sm:text-sm font-medium ${msg.appreciation.color} bg-black/80 backdrop-blur-sm border border-emerald-500/20 px-3 sm:px-4 py-1.5 sm:py-2 rounded-lg shadow-xl`}
                    >
                      {msg.appreciation.text}
                    </motion.div>
                  )}
              </div>
            </motion.div>
          ))}

          {isTyping && (
            <motion.div
              className="text-emerald-400/80 text-xs sm:text-sm flex items-center space-x-2"
              animate={{ opacity: [0.5, 1, 0.5] }}
              transition={{ repeat: Infinity, duration: 1 }}
            >
              <div className="w-1.5 h-1.5 rounded-full bg-emerald-400/80 animate-pulse"></div>
              <div className="w-1.5 h-1.5 rounded-full bg-emerald-400/60 animate-pulse delay-75"></div>
              <div className="w-1.5 h-1.5 rounded-full bg-emerald-400/40 animate-pulse delay-150"></div>
              <span>AI is typing...</span>
            </motion.div>
          )}

          <div ref={messagesEndRef} />
        </div>

        <div className="w-full sticky bottom-0 p-3 sm:p-4 md:p-5 bg-gradient-to-t from-black via-black/95 to-black/90 px-4 sm:px-6 md:px-8 py-3 sm:py-4 md:py-5 z-20 shadow-[0_-4px_20px_rgba(16,185,129,0.15)] backdrop-blur-md">
          <div className="flex items-center space-x-2 sm:space-x-3 w-full max-w-[800px] mx-auto">
            <div className="flex-1 relative group">
              <div className="absolute -inset-[1px] bg-gradient-to-r from-emerald-500/10 via-emerald-500/20 to-emerald-500/10 rounded-xl blur-lg opacity-0 group-hover:opacity-100 transition-all duration-500"></div>

              {isTyping && (
                <div className="absolute left-4 top-1/2 -translate-y-1/2 flex items-center space-x-1 z-10">
                  <motion.div
                    className="w-1.5 h-1.5 rounded-full bg-emerald-400/80"
                    animate={{ scale: [1, 1.2, 1] }}
                    transition={{ repeat: Infinity, duration: 1 }}
                  />
                  <motion.div
                    className="w-1.5 h-1.5 rounded-full bg-emerald-400/60"
                    animate={{ scale: [1, 1.2, 1] }}
                    transition={{ repeat: Infinity, duration: 1, delay: 0.2 }}
                  />
                  <motion.div
                    className="w-1.5 h-1.5 rounded-full bg-emerald-400/40"
                    animate={{ scale: [1, 1.2, 1] }}
                    transition={{ repeat: Infinity, duration: 1, delay: 0.4 }}
                  />
                </div>
              )}

              <div className="relative">
                <div className="absolute inset-0 bg-gradient-to-r from-emerald-950/30 via-gray-900/40 to-emerald-950/30 rounded-xl"></div>
                <div className="absolute inset-[1px] bg-gradient-to-b from-black/80 to-black/40 rounded-[11px]"></div>
                <input
                  type="text"
                  className={`relative w-full px-3 sm:px-4 py-2.5 sm:py-3 bg-transparent text-xs sm:text-sm text-gray-200 placeholder-gray-500 font-normal tracking-wide transition-all duration-300 rounded-xl border border-emerald-500/30 focus:border-emerald-500/50 focus:ring-2 focus:ring-emerald-500/20 shadow-[0_0_15px_rgba(16,185,129,0.15)] focus:shadow-[0_0_25px_rgba(16,185,129,0.25)] backdrop-blur-sm focus:outline-none ${
                    isTyping ? "pl-12 sm:pl-14" : "pl-3 sm:pl-4"
                  }`}
                  placeholder="Type your message..."
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && handleSend()}
                />
                <div className="absolute inset-0 pointer-events-none rounded-xl bg-gradient-to-t from-emerald-500/5 via-transparent to-emerald-500/5"></div>
              </div>
            </div>

            <motion.button
              onClick={handleSend}
              className="relative p-2.5 sm:p-3 bg-gradient-to-b from-emerald-950/50 via-black to-emerald-950/50 border border-emerald-500/30 rounded-xl transition-all duration-300 hover:border-emerald-500/50 hover:shadow-[0_0_20px_rgba(16,185,129,0.2)] group overflow-hidden"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <div className="absolute inset-0 bg-gradient-to-r from-emerald-500/10 via-emerald-500/20 to-emerald-500/10 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
              <Send
                size={16}
                className="text-emerald-400/70 group-hover:text-emerald-400 transition-colors duration-300 relative z-10"
              />
            </motion.button>
          </div>
        </div>
      </div>
    </div>
  );
}